"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PipelineState = void 0;
var ObjectResourceStore_1 = require("./ObjectResourceStore");
var makeBitmap_1 = require("../util/makeBitmap");
var SvgRenderingContext_1 = require("../rendering/SvgRenderingContext");
var CanvasRenderingContext_1 = require("../rendering/CanvasRenderingContext");
/**
 * The stateful part of the rendering pipeline. Stores object cache data and asynchronously
 * loaded resources. Each object gets its own {@link ObjectResourceStore}. If an object is
 * deleted, {@code Pipeline.dispose} should be called to clean up these resources.
 */
var PipelineState = /** @class */ (function () {
    function PipelineState(canvas, pipeline) {
        this.canvas = canvas;
        this.pipeline = pipeline;
        this._objectSnapshots = {};
        this._objectResourceStores = {};
        this.ctx = canvas.tagName.toLowerCase() === "canvas"
            ? new CanvasRenderingContext_1.CanvasRenderingContext(canvas)
            : new SvgRenderingContext_1.SvgRenderingContext(canvas);
    }
    /**
     * Gets the resource store of an object.
     *
     * @param object The object whose resource store to get.
     */
    PipelineState.prototype.storeOf = function (object) {
        return this._getOrCreateObjectResourceStore(object.id);
    };
    /**
     * Used to draw an object on a cached off-screen sub-canvas. The sub-canvas size will
     * exactly equal the object's paint size.
     *
     * This has performance benefits and functional benefits. Objects with shadows are
     * expensive to draw, so they benefit from caching. Objects with alpha < 1 can be
     * drawn opaquely on the sub-canvas, then have alpha applied in one pass when
     * placed on the main canvas, which prevents alpha blending of drawing steps.
     *
     * @param object The object. A snapshot will be stored for comparison later.
     * @param paintIfChanged The (re)paint logic. First argument is previous object data.
     */
    PipelineState.prototype.updateSubcanvas = function (object, paintIfChanged) {
        var store = this._getOrCreateObjectResourceStore(object.id);
        store.layers.resizeTo(this.pipeline.paintSizeOf(object));
        var previousData = this._swapSnapshot(object);
        paintIfChanged(previousData, store);
        return store.layers.result.canvas;
    };
    /**
     * Takes a snapshot of an object and returns the previous snapshot, if any.
     */
    PipelineState.prototype._swapSnapshot = function (object) {
        var previous = this._objectSnapshots[object.id];
        this._objectSnapshots[object.id] = __assign(__assign({}, object), { custom: __assign({}, object.custom) });
        return previous;
    };
    /**
     * Gets the resource store of an object, allocating a new one if needed.
     */
    PipelineState.prototype._getOrCreateObjectResourceStore = function (id) {
        var store = this._objectResourceStores[id];
        if (!store) {
            store = this._createObjectResourceStore(id);
            this._objectResourceStores[id] = store;
        }
        return store;
    };
    /**
     * Instantiates a new resource store.
     */
    PipelineState.prototype._createObjectResourceStore = function (id) {
        var _this = this;
        return new ObjectResourceStore_1.ObjectResourceStore(this.pipeline.device, this.pipeline.onResourceChanged, function (e) { return _this.pipeline.onResourceError("Resource error for object \"" + id + "\": " + e); });
    };
    /**
     * Returns a promise for the loading of ALL resources that are <em>currently</em> in
     * the state. Intended for server-side rendering. Note: adding or removing resources
     * after this method is called does not affect the returned promise.
     */
    PipelineState.prototype.promiseAll = function () {
        var promises = Object
            .values(this._objectResourceStores)
            .map(function (s) { return s.promiseAll(); });
        return Promise.all(promises);
    };
    /**
     * Gets the object IDs for which resources are currently being held.
     */
    PipelineState.prototype.getActiveObjectIds = function () {
        return Object.keys(this._objectSnapshots);
    };
    /**
     * Attempts to get the existing {@link Bitmap} from the state, if nonexistent
     * creates a new one and returns it.
     */
    PipelineState.prototype.getOrCreateBitmap = function () {
        var _a;
        return (_a = this._cachedBitmap) !== null && _a !== void 0 ? _a : (this._cachedBitmap = makeBitmap_1.makeBitmap(this.ctx));
    };
    /**
     * Disposes all resources held for objects.
     */
    PipelineState.prototype.disposeAll = function () {
        var e_1, _a;
        try {
            for (var _b = __values(this.getActiveObjectIds()), _c = _b.next(); !_c.done; _c = _b.next()) {
                var id = _c.value;
                this.disposeById(id);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    /**
     * Disposes all resources held for an object.
     *
     * @param object The object being disposed.
     */
    PipelineState.prototype.dispose = function (object) {
        this.disposeById(object.id);
    };
    /**
     * Disposes all resources held for an object.
     *
     * @param id The ID of the object being disposed.
     */
    PipelineState.prototype.disposeById = function (id) {
        delete this._objectSnapshots[id];
        delete this._objectResourceStores[id];
    };
    /**
     * Invalidates cached bitmap, making sure a reraster is required when needed again.
     */
    PipelineState.prototype.invalidateCachedBitmap = function () {
        this._cachedBitmap = null;
    };
    return PipelineState;
}());
exports.PipelineState = PipelineState;
//# sourceMappingURL=PipelineState.js.map